import { useEffect, useMemo, useState } from "react";

const collator = new Intl.Collator("sv");

function compareByKey(key, ascending) {
  return (a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    let comparison = 0;
    if (typeof valueA === "string" && typeof valueB === "string") {
      comparison = collator.compare(valueA, valueB);
    } else if (valueA < valueB) {
      comparison = -1;
    } else if (valueA > valueB) {
      comparison = 1;
    }
    return ascending ? comparison : comparison * -1;
  };
}

export function useTableControls(data, searchableFields, initialPageSize = 50) {
  const [sortKey, setSortKey] = useState(null);
  const [sortAscending, setSortAscending] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const query = debouncedQuery.trim().toLowerCase();

  const filteredData = useMemo(() => {
    if (!query) {
      return data;
    }
    return data.filter((row) =>
      searchableFields.some((field) =>
        String(row[field] ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [data, query, searchableFields]);

  const sortedData = useMemo(() => {
    if (!sortKey) {
      return filteredData;
    }
    return [...filteredData].sort(compareByKey(sortKey, sortAscending));
  }, [filteredData, sortKey, sortAscending]);

  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;

  const pageRows = sortedData.slice(startIndex, startIndex + pageSize);

  const sortByColumn = (key) => {
    if (sortKey === key) {
      setSortAscending((ascending) => !ascending);
    } else {
      setSortKey(key);
      setSortAscending(true);
    }
    setCurrentPage(1);
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) {
      return "";
    }
    return sortAscending ? " ▲" : " ▼";
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return {
    pageRows,
    searchQuery,
    handleSearchChange,
    sortByColumn,
    sortIndicator,
    pagination: {
      currentPage: safePage,
      totalPages,
      totalItems,
      pageSize,
      onPageChange: setCurrentPage,
      onPageSizeChange: handlePageSizeChange,
    },
  };
}
